import { HeuristicLine } from "./sectionHeuristics";

const BLOCK_SELECTOR = "p, h1, h2, h3, h4, h5, h6, li, td, th";
const HEADING_TAGS = new Set(["H1", "H2", "H3", "H4", "H5", "H6"]);
const BOLD_FONT_WEIGHT_THRESHOLD = 600;
const DOCX_HEADING_CLASS = /_heading[1-9]$/i;

/**
 * docx-preview renders a Word heading as a `p` carrying the paragraph's style id as a class
 * (`docx-preview_heading2`) rather than as an `h2`, so the tag name does not recognise one. Neither does
 * the font weight: Word's own heading styles are usually coloured and larger rather than bold. The class
 * is the only signal left. Takes the class attribute rather than the element so it stays unit-testable.
 */
export function isDocxHeadingClassName(className: string): boolean {
    return className.split(/\s+/).some((name) => DOCX_HEADING_CLASS.test(name));
}

/**
 * Turns a raw browser selection into a clause a user would recognise: the line breaks a renderer inserts
 * to lay text out are not part of the sentence, and neither are the hyphens it adds when breaking a word.
 * `joinLines` says whether those breaks are layout (PDF text layer) or real paragraph breaks (Word).
 */
export function normalizeSelectedText(raw: string, options: { joinLines: boolean }): string {
    const withPlainSpaces = raw.replace(/[\u00A0\u2007\u202F]/g, " ").replace(/\r\n?/g, "\n");
    const deHyphenated = withPlainSpaces.replace(/(\p{L})-\n\s*(\p{L})/gu, "$1$2");

    const withoutLayoutBreaks = options.joinLines
        ? deHyphenated.replace(/\n+/g, " ")
        : deHyphenated
              .split("\n")
              .map((line) => line.trim())
              .filter((line) => line.length > 0)
              .join("\n");

    return withoutLayoutBreaks
        .replace(/[ \t]{2,}/g, " ")
        .replace(/ ([,.;:!?])/g, "$1")
        .trim();
}

function isInsideBoldElement(block: HTMLElement): boolean {
    if (block.querySelector("b, strong")) {
        return true;
    }

    // docx-preview emits generated class names and inline styles rather than semantic tags, so the
    // font weight on the element itself is the only reliable signal.
    const inlineWeight = block.style.fontWeight;
    if (!inlineWeight) {
        return false;
    }
    if (inlineWeight === "bold" || inlineWeight === "bolder") {
        return true;
    }
    const numericWeight = Number(inlineWeight);
    return !Number.isNaN(numericWeight) && numericWeight >= BOLD_FONT_WEIGHT_THRESHOLD;
}

/**
 * The block-level lines of a document up to and including the block holding `startNode`, each carrying
 * whether it renders as a heading or in bold, with the index of the starting block.
 */
export function collectPrecedingBlockLines(root: HTMLElement, startNode: Node): { lines: Array<HeuristicLine>; startLineIndex: number } {
    const blocks = Array.from(root.querySelectorAll<HTMLElement>(BLOCK_SELECTOR));
    const lines: Array<HeuristicLine> = [];
    let startLineIndex = -1;

    for (const block of blocks) {
        // Nested blocks (a `li` inside a `td`) would otherwise contribute their text twice.
        if (block.querySelector(BLOCK_SELECTOR)) {
            continue;
        }

        if (block === startNode || block.contains(startNode)) {
            startLineIndex = lines.length;
        }

        lines.push({
            text: block.textContent ?? "",
            isHeading: HEADING_TAGS.has(block.tagName) || isDocxHeadingClassName(block.className),
            isBold: isInsideBoldElement(block),
        });

        if (startLineIndex !== -1) {
            break;
        }
    }

    return { lines, startLineIndex: startLineIndex === -1 ? lines.length - 1 : startLineIndex };
}
